import { ProductsService } from 'src/app/services/products.service';
import { environment } from 'src/environments/environment';
import { IClient } from './models/client.model';
import { DepartmentService } from './services/department.service';
import { Component, OnDestroy, OnInit, HostListener, PLATFORM_ID, Inject } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { DEFAULT_LANGUAGE } from 'src/app/shared/utils';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatSidenavModule } from '@angular/material/sidenav';
import { CommonModule, isPlatformBrowser, Location } from '@angular/common';
import { NavigationEnd, Router, RouterLink, RouterOutlet } from '@angular/router';
import { v4 as uuidv4 } from 'uuid';
import { filter, take, takeUntil } from 'rxjs/operators';
import { UntypedFormControl, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { TokenService } from './services/token.service';
import { Subject } from 'rxjs';
import { PixelEvent } from '@models/pixel.model';
import { MarketingService } from '@services/marketing.service';
import { encryptTextSHA256 } from '@shared/encryptText';
import { AppStateUser } from './state/user/user.reducer';
import { Store } from '@ngrx/store';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { HomeToolbarComponent } from '@shared/components/home-toolbar/home-toolbar.component';
import { HomeFooterComponent } from '@shared/components/home-footer/home-footer.component';
import { NewsletterComponent } from '@shared/components/newsletter/newsletter.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ICONS } from '@shared/icons';
import { MatButtonModule } from '@angular/material/button';
import { Department } from '@models/departments.model';
import { Category } from '@models/categories.model';

declare let gtag: any;
declare let pintrk: Function;
declare let fbq: Function;

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: true,
  imports: [
    RouterLink,
    RouterOutlet,
    CommonModule,
    TranslateModule,
    MatIconModule,
    HomeToolbarComponent,
    HomeFooterComponent,
    NewsletterComponent,
    MatSidenavModule,
    MatProgressSpinnerModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule
  ]
})
export class AppComponent implements OnInit, OnDestroy {

  public auth: boolean;
  public home: boolean;
  public club: boolean;
  public paypal: boolean;
  public loading = false;
  public purchase: boolean;
  private subs = new Subject<void>();
  public displayCategories = false;
  public params: { name: string; value: string }[];
  public client: IClient;
  public isMobile: boolean;
  public copy = false;
  public siteStatus = false;
  public categories: Category[] = [];
  public departments: Department[] = [];
  public currentDepartment: Department | null;
  public viewToolbar = false;
  title = 'AlSuper-Cliente-Frontend';
  public appStoreUrl: string = environment.appStore;
  public googlePlayUrl: string = environment.googlePlay;
  public urlStore: string;

  public password = new UntypedFormControl('', [Validators.required]);
  public banner: string;
  public visibleBanner = true;
  public categoriesLoading = false;
  public eventBusSub?: Subscription;
  private defaultStore = environment.defaultStore;

  @HostListener('window:resize', ['$event'])
  onResize(): void {
    if (isPlatformBrowser(this.platformId)) this.isMobile = window.innerWidth <= 800;
  }

  constructor(
    private router: Router,
    private location: Location,
    private sanitizer: DomSanitizer,
    private translate: TranslateService,
    private iconRegistry: MatIconRegistry,
    private departmentService: DepartmentService,
    @Inject(PLATFORM_ID) private platformId: string,
    private productsService: ProductsService,
    private tokenService: TokenService,
    private marketingService: MarketingService,
    private storeUser: Store<AppStateUser>
  ) {

    if (isPlatformBrowser(this.platformId)) {
      const googleAnalyticsScript = document.createElement('script');
      googleAnalyticsScript.async = true;
      googleAnalyticsScript.setAttribute('type', 'text/javascript');
      googleAnalyticsScript.setAttribute('src', `${environment.googleAnalytics.url}${environment.googleAnalytics.id}`);
      document.body.appendChild(googleAnalyticsScript);
    }

    const navEndEvents$ = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    );
    navEndEvents$.pipe(takeUntil(this.subs)).subscribe((event: any) => {
      if (isPlatformBrowser(this.platformId)) {
        gtag('config', environment.googleAnalytics.id, {
          'page_path': event.urlAfterRedirects
        });

        //* Pinterest tag
        pintrk('track', 'pagevisit', {
          event_id: uuidv4(),
        });
        fbq('track', 'PageView');
        this.facebookEvent();
      }
    });
    this.getClientData();

    this.translate.setDefaultLang(DEFAULT_LANGUAGE);
    this.translate.use(DEFAULT_LANGUAGE);

    //* Se cargan los íconos del sitio desde @shared/data/icons.ts
    Object.keys(ICONS).forEach(name => {
      this.iconRegistry.addSvgIcon(name, this.sanitizer.bypassSecurityTrustResourceUrl(`${ICONS[name as keyof typeof ICONS]}`));
    });

    this.router.events.pipe(takeUntil(this.subs)).subscribe(() => {
      this.home = this.location.path() === '' || this.location.path() === '?utm_source=Ofertia&utm_medium=Reach&utm_campaign=AlsuperEngagement' ? true : false;
      this.paypal = this.location.path().includes('paypal') || this.location.path().includes('pagos/approve') || this.location.path().includes('alsuperapp');
      this.auth = this.location.path().includes('autenticacion') || this.location.path().includes('webview-help') || this.location.path().includes('webview-tyc') || this.location.path().includes('webview-aviso') || this.location.path().includes('cuenta-eliminada');
      this.purchase = this.location.path().includes('compra-en-proceso');
    });
    this.productsService.setCopyLink$.pipe(takeUntil(this.subs)).subscribe((res: boolean) => {
      if (res) {
        if (isPlatformBrowser(this.platformId)) {
          const url = window.location.href;
          this.copyText(url);
        }
      }
    });
    if (isPlatformBrowser(this.platformId)) {
      const userAg = navigator.userAgent;
      if (userAg.toLowerCase().includes('android')) {
        this.urlStore = this.googlePlayUrl;
      } else {
        this.urlStore = this.appStoreUrl;
      }
    }
  }

  private async copyText(val: string): Promise<void> {
    if (isPlatformBrowser(this.platformId)) {
      await navigator.clipboard.writeText(val);
      this.copy = true;
      setTimeout(() => {
        this.copy = false;
      }, 2500);
    }
  }

  public closeToolbar(): void {
    this.viewToolbar = true;
  }

  public getDepartments(): void {
    const storeId = JSON.parse(localStorage.getItem('address') || '{}').store;
    this.departmentService.getDepartments(storeId ?? this.defaultStore).pipe(take(1)).subscribe(({
      next: ({ data }) => {
        this.departments = data.map((department: Department) => {
          department.show = false;
          const nameArr = department.description.split(' ');
          const desc = nameArr.join('-');
          department.department_url = `departamento/${desc.toLowerCase()}-${department.department_id}`;
          return department;
        });
      }
    }));
  }

  public async showCategories(department: Department, index: number): Promise<void> {
    this.categoriesLoading = true;
    const { data } = await this.departmentService.getCategories(department.department_id.toString());

    this.categories = data.categories;
    const nameArr = department.description.split(' ');
    const desc = nameArr.join('-');
    this.categories.forEach((category: Category) => {
      const departmentId = `${desc.toLowerCase()}-${department.department_id}`;
      category.queryParams = {
        departamento_id: departmentId,
        categoria: category.category_name,
        categoria_id: category.category_id
      };
    });
    this.departments.forEach((item: any, arrIndex: number) => {
      item.show = false;
      if (arrIndex === index) {
        this.currentDepartment = null;
        if (item.show) {
          this.currentDepartment = null;
          item.show = false;
        } else {
          this.currentDepartment = item;
          item.show = true;
        }
      }
    });
    this.categoriesLoading = false;
  }

  public getClientData(): void {
    this.storeUser.select('user').pipe(takeUntil(this.subs)).subscribe(({ data }) => {
      this.client = data;
    });
  }

  private facebookEvent() {
    try {
      if (isPlatformBrowser(this.platformId)) {
        const pixelEvent: PixelEvent = {
          data: {
            event_name: 'PageView',
            event_time: Date.now(),
            action_source: 'website',
            user_data: {
              em: encryptTextSHA256(this.client?.email) ?? '',
              ph: encryptTextSHA256(this.client?.cellphone) ?? '',
              fbp: document.cookie.match('(^|;)\\s*' + '_fbp' + '\\s*=\\s*([^;]+)')?.pop() || '',
              fbc: document.cookie.match('(^|;)\\s*' + '_fbc' + '\\s*=\\s*([^;]+)')?.pop() || '',
              external_id: encryptTextSHA256(this.client?.user_id.toString()) ?? ''
            },
          }
        };
        this.marketingService.pixelCall(pixelEvent);
      }
    } catch (error) {
    }
  }

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.isMobile = window.innerWidth <= 800;
    }
  }

  public logout(): void {
    this.tokenService.signOut();
  }

  @HostListener('window:scroll', ['$event'])
  public onScrollToolbarBaner($event: any): void {
    if (isPlatformBrowser(this.platformId)) {
      const verticalOffsetBanner = window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;
      if (verticalOffsetBanner >= 5) {
        this.visibleBanner = false;
      } else if (verticalOffsetBanner <= 5) {
        this.visibleBanner = true;
      }
    }
  }

  ngOnDestroy(): void {
    if (this.eventBusSub) {
      this.eventBusSub.unsubscribe();
    }
    this.subs.next();
    this.subs.complete();
  }
}
